import React, { useState, useCallback, useEffect, useRef } from 'react';

function getCsrfToken() {
  const meta = document.querySelector('meta[name="csrf-token"]');
  return meta ? meta.getAttribute('content') : '';
}

function BackupCodeChallenge({ verifyUrl, authenticatorUrl, loginUrl, translations }) {
  const [code, setCode] = useState('');
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => {
    if (inputRef.current) inputRef.current.focus();
  }, []);

  const handleCodeChange = useCallback((e) => {
    const value = e.target.value.replace(/[^0-9a-f-]/gi, '').slice(0, 9);
    setCode(value);
    setError(null);
  }, []);

  const handleSubmit = useCallback((e) => {
    if (e) e.preventDefault();
    if (code.length < 9 || submitting) return;

    setSubmitting(true);
    setError(null);

    fetch(verifyUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-Token': getCsrfToken(),
        Accept: 'application/json'
      },
      body: JSON.stringify({ code: code })
    })
      .then((res) => {
        if (res.status === 401) {
          window.location.href = loginUrl;
          return null;
        }
        return res.json();
      })
      .then((data) => {
        if (!data) return;

        if (data.success) {
          const redirectUrl = data.redirect_url || '/';

          if (window.Turbolinks) {
            Turbolinks.visit(redirectUrl);
          } else {
            window.location.href = redirectUrl;
          }
        } else {
          setError(data.message || translations.invalid_code);
          setCode('');
          setSubmitting(false);

          if (inputRef.current) inputRef.current.focus();
        }
      })
      .catch(() => {
        setError(translations.invalid_code);
        setCode('');
        setSubmitting(false);
      });
  }, [code, submitting, verifyUrl, loginUrl, translations]);

  return (
    <div className="two-factor-challenge">
      <div className="two-factor-challenge__header login-card__header">
        <div className="two-factor-challenge__shield two-factor-challenge__shield--key">
          <i className="zmdi zmdi-key" />
        </div>
        <h2 className="two-factor-challenge__title">{translations.title}</h2>
        <p className="two-factor-challenge__desc">{translations.description}</p>
      </div>

      <div className="alert alert-warning py-2 mb-3">
        <i className="zmdi zmdi-alert-triangle mr-1" />
        {translations.warning}
      </div>

      {error && <div className="alert alert-danger py-2 mb-3">{error}</div>}

      <form onSubmit={handleSubmit}>
        <div className="form-group">
          <input
            ref={inputRef}
            type="text"
            className="form-control two-factor-code-input two-factor-code-input--large"
            value={code}
            onChange={handleCodeChange}
            placeholder={translations.placeholder}
            maxLength={9}
            disabled={submitting}
          />
          <i className="form-group__bar" />
        </div>

        <button
          type="submit"
          className="btn btn-primary login-btn w-100 mt-4"
          disabled={submitting || code.length < 9}
        >
          {submitting ? translations.verifying : translations.verify_button}
          {!submitting && <span className="login-btn__arrow">&rarr;</span>}
        </button>
      </form>

      <div className="two-factor-challenge__links login-card__links mt-4">
        <a href={authenticatorUrl} className="d-block text-center mb-2">
          {translations.use_authenticator}
        </a>
        <a href={loginUrl} className="d-block text-center text-muted">
          {translations.back_to_login}
        </a>
      </div>
    </div>
  );
}

export default BackupCodeChallenge;
